/**
 * Generate Invoice dialog — EB-3883 page UI cleanup.
 *
 * Hosts the former "Manual Invoice" section's form (the 4 date controls
 * `basicFeeFrom/Until`, `serviceFeeFrom/Until` + Generate/Clean actions),
 * moved verbatim out of `invoice.component.ts` into a MatDialog launched from
 * the sub-hero "Manual Invoice" button.
 *
 * Mirrors `AddBillingDialogComponent`'s submit/error contract:
 *   - On Generate success → close with a truthy result so the parent refreshes
 *     the Invoices list + shows a toast.
 *   - On error → stay open, surface an error toast, re-enable the form.
 *
 * The dialog owns the generation call (calls `InvoiceService.generateInvoice`
 * directly) — same as `add-billing-dialog` calls `CustomerBillingService.create`.
 * `customerId` + the date-input `min` are supplied via `MAT_DIALOG_DATA`.
 */
import { Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
  ToastService,
} from '../../../ui';
import { InvoiceService } from '../invoice.service';

/** Input contract — resolved from `MAT_DIALOG_DATA`. */
export interface GenerateInvoiceDialogData {
  customerId: number;
  /** ISO `YYYY-MM-DD` lower bound for the native date inputs (old CMT: today). */
  minDateStr: string;
}

/** Truthy close result tells the parent to refresh the Invoices list + toast. */
export interface GenerateInvoiceResult {
  generated: true;
}

@Component({
  selector: 'cmt-generate-invoice-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL,
    ...HLM_FORM_FIELD,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmSpinnerComponent,
  ],
  templateUrl: './generate-invoice-dialog.component.html',
})
export class GenerateInvoiceDialogComponent {
  readonly dialogRef = inject(
    MatDialogRef<GenerateInvoiceDialogComponent, GenerateInvoiceResult | null>,
  );
  readonly data: GenerateInvoiceDialogData = inject(MAT_DIALOG_DATA);

  private readonly invoiceService = inject(InvoiceService);
  private readonly toast = inject(ToastService);

  readonly generating = signal(false);

  /** Invoice generation form — string dates for native date inputs. */
  readonly generateForm = new FormGroup({
    basicFeeFrom: new FormControl<string | null>(null, Validators.required),
    basicFeeUntil: new FormControl<string | null>(null, Validators.required),
    serviceFeeFrom: new FormControl<string | null>(null, Validators.required),
    serviceFeeUntil: new FormControl<string | null>(null, Validators.required),
  });

  /** Generate → POST plannedInvoice; close on success, stay open on error. */
  generateInvoice(): void {
    if (this.generating() || this.generateForm.invalid) return;

    this.generating.set(true);

    const vals = this.generateForm.value;
    const payload = {
      basicFeeFrom: vals.basicFeeFrom!,
      basicFeeUntil: vals.basicFeeUntil!,
      serviceFeeFrom: vals.serviceFeeFrom!,
      serviceFeeUntil: vals.serviceFeeUntil!,
    };

    this.invoiceService.generateInvoice(this.data.customerId, payload).subscribe({
      next: () => {
        this.generating.set(false);
        this.dialogRef.close({ generated: true });
      },
      error: () => {
        this.generating.set(false);
        this.toast.open('Failed to schedule invoice', 'OK', { duration: 5000, tone: 'error' });
      },
    });
  }

  /** Reset all four date controls (the old section's "Clean" button). */
  clean(): void {
    this.generateForm.reset();
  }

  /** Cancel closes with null so the caller's `afterClosed()` sees a falsy result. */
  cancel(): void {
    this.dialogRef.close(null);
  }
}
